/**
 * Activity Ticker — ephemeral contextual message system.
 *
 * triggerTicker(message) — fire-and-forget, adds to queue
 * useTickerMessage() — React hook, returns current message or null
 */
import { useState, useEffect } from 'react';

type Listener = (msg: string | null) => void;

let _listeners: Listener[] = [];
const _queue: string[] = [];
let _current: string | null = null;
let _timer: ReturnType<typeof setTimeout> | null = null;

function notify(): void {
  for (const fn of _listeners) fn(_current);
}

function processQueue(): void {
  if (_current || _queue.length === 0) return;
  _current = _queue.shift() ?? null;
  notify();
  // Stay visible 3.5 seconds, then fade out (200ms handled by CSS)
  _timer = setTimeout(() => {
    _current = null;
    notify();
    // After fade-out, process next in queue
    setTimeout(processQueue, 250);
  }, 3500);
}

export function triggerTicker(message: string | null | undefined): void {
  if (!message) return;
  _queue.push(message);
  if (!_current) processQueue();
}

export function useTickerMessage(): string | null {
  const [msg, setMsg] = useState<string | null>(_current);

  useEffect(() => {
    _listeners.push(setMsg);
    return () => {
      _listeners = _listeners.filter((fn) => fn !== setMsg);
    };
  }, []);

  return msg;
}
